/**
 * Where a transfer starts, and its coordinates.
 *
 * The pickup is the place of death almost every time, so `pickup_address` is an
 * exception rather than a second field to maintain: null means "use the place of
 * death". Only a case collected somewhere else populates it.
 */
import { LatLng } from './zone';

export type Address = {
  adresse?: string | null;
  municipalite?: string | null;
  province?: string | null;
  codePostal?: string | null;
  pays?: string | null;
  lat?: number | null;
  lng?: number | null;
  /**
   * The address these coordinates were resolved from.
   *
   * Without it, coordinates outlive the address they belong to: editing the
   * text fields by hand does not touch lat/lng, so a case retyped from
   * Pointe-Claire to Saint-Jean-sur-Richelieu keeps pointing at Pointe-Claire —
   * and the zone test, which reads coordinates, calls it included when it
   * should be charged.
   */
  geocoded_from?: string | null;
};

export type Funeral = {
  place_of_death?: string | null;
  place_of_death_address?: Address | null;
  pickup_address?: Address | null;
};

export type PickupLocation = {
  address: Address;
  /** One line, for showing what was measured from. */
  label: string;
  /** True when collection is somewhere other than the place of death. */
  overridden: boolean;
};

/**
 * One line: street, city, province, postal code. Country and coordinates are
 * left out — every case is in Canada, and lat/lng is bookkeeping, not address.
 *
 * Legacy rows hold the address as a plain string, so a string is returned
 * trimmed rather than read as an object (which would print "[object Object]"
 * or nothing at all).
 */
export const formatAddress = (a?: Address | string | null): string => {
  if (typeof a === 'string') return a.trim();
  if (!a || typeof a !== 'object') return '';
  return [a.adresse, a.municipalite, a.province, a.codePostal]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ');
};

/**
 * "Facility, street, city, province, postal code" — the place-of-death label
 * followed by its address, for messages to partners. The label is dropped when
 * the address already contains it (a home death often has the street as label).
 */
export const formatPlaceOfDeath = (
  label?: string | null,
  address?: Address | string | null,
): string => {
  const name = (label ?? '').trim();
  const addr = formatAddress(address);
  if (name && addr.toLowerCase().includes(name.toLowerCase())) return addr;
  return [name, addr].filter(Boolean).join(', ');
};

/**
 * Where the driver collects: the pickup override when staff set one (the
 * facility name belongs to the place of death, so it is left off), otherwise
 * the place of death with its name.
 */
export const formatPickupForDispatch = (funeral: {
  place_of_death?: string | null;
  place_of_death_address?: Address | string | null;
  pickup_address?: Address | string | null;
}): string =>
  formatAddress(funeral.pickup_address) ||
  formatPlaceOfDeath(funeral.place_of_death, funeral.place_of_death_address);

/** The address a transfer actually starts from, and whether it is an exception. */
export function pickupLocation(funeral: Funeral): PickupLocation | null {
  const override = funeral.pickup_address;
  if (override && formatAddress(override)) {
    return { address: override, label: formatAddress(override), overridden: true };
  }

  const pod = funeral.place_of_death_address;
  if (pod && formatAddress(pod)) {
    return { address: pod, label: formatAddress(pod), overridden: false };
  }

  // A facility name with no address is not enough to price from. Say so rather
  // than geocoding "Toronto General" and hoping.
  return null;
}

/**
 * Coordinates, but only when they still belong to this address.
 *
 * Stale coordinates are worse than none: none shows "not checked" and asks for
 * a click, while stale ones confidently price the wrong place. So coordinates
 * whose `geocoded_from` no longer matches the address are treated as absent.
 *
 * Coordinates with no `geocoded_from` at all predate this check and cannot be
 * verified, so they are also treated as absent. That costs one geocode per old
 * case and is the safe direction — the alternative is trusting a number we have
 * already seen be wrong.
 */
export const coordsOf = (a: Address): LatLng | null => {
  if (a.lat == null || a.lng == null) return null;
  if (!a.geocoded_from) return null;
  return a.geocoded_from === formatAddress(a) ? { lat: a.lat, lng: a.lng } : null;
};

/**
 * Coordinates for an address that has none.
 *
 * Only 326 of 651 cases have a structured place of death and none have
 * coordinates, so most existing cases land here. Uses the Maps geocoder already
 * loaded for the address autocomplete.
 *
 * Rejects on a low-confidence result rather than returning one. A geocode that
 * lands on a city centroid would place a rural address kilometres from where it
 * belongs, and the whole charge is built on that point.
 */
export function geocode(address: Address): Promise<LatLng> {
  const g = (window as any).google;
  if (!g?.maps?.Geocoder) return Promise.reject(new Error('Maps is not loaded'));

  const text = formatAddress(address);
  if (!text) return Promise.reject(new Error('No address to look up'));

  return new Promise((resolve, reject) => {
    new g.maps.Geocoder().geocode(
      { address: text, componentRestrictions: { country: 'CA' } },
      (results: any[], status: string) => {
        if (status !== 'OK' || !results?.length) {
          reject(new Error(status || 'Address not found'));
          return;
        }
        const best = results[0];
        // APPROXIMATE means Google interpolated to a region rather than finding
        // the address — precise enough for a map pin, not for a bill.
        if (best.geometry?.location_type === 'APPROXIMATE') {
          reject(new Error('Address is too vague to place accurately'));
          return;
        }
        const loc = best.geometry?.location;
        if (!loc) {
          reject(new Error('No coordinates in the result'));
          return;
        }
        resolve({ lat: loc.lat(), lng: loc.lng() });
      },
    );
  });
}
